import React from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  readTime: number;
  image: string;
  imageAlt: string;
}

interface BlogCardProps {
  post: BlogPost;
  featured?: boolean;
}

const BlogCard = ({ post, featured = false }: BlogCardProps) => {
  return (
    <article
      className={`group overflow-hidden rounded-xl bg-card shadow-warm transition-smooth hover:shadow-warm-lg ${
        featured ? 'md:flex md:gap-6' : ''
      }`}
    >
      <Link href={`/blog/${post.slug}`} className="block">
        <div
          className={`relative overflow-hidden ${featured ? 'md:w-96 md:flex-shrink-0' : 'h-56'}`}
        >
          <AppImage
            src={post.image}
            alt={post.imageAlt}
            className="h-full w-full object-cover transition-smooth group-hover:scale-105"
          />
          <div className="absolute left-4 top-4">
            <span className="rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground shadow-warm">
              {post.category}
            </span>
          </div>
        </div>
      </Link>

      <div className={`p-6 ${featured ? 'flex-1' : ''}`}>
        {/* Date + Read Time */}
        <div className="mb-3 flex items-center gap-4 text-sm text-muted-foreground">
          <time dateTime={post.date} className="flex items-center gap-1.5">
            <Icon name="CalendarIcon" size={16} variant="outline" />
            {new Date(post.date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </time>

          <span className="flex items-center gap-1.5">
            <Icon name="ClockIcon" size={16} variant="outline" />
            {post.readTime} min read
          </span>
        </div>

        {/* Title */}
        <Link href={`/blog/${post.slug}`}>
          <h3
            className={`mb-3 font-heading font-semibold text-foreground transition-smooth group-hover:text-primary ${
              featured ? 'text-2xl' : 'text-xl'
            }`}
          >
            {post.title}
          </h3>
        </Link>

        {/* Excerpt */}
        <p className="mb-4 text-muted-foreground line-clamp-3">{post.excerpt}</p>

        {/* Author + CTA */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">By {post.author}</p>
          </div>

          <Link
            href={`/blog/${post.slug}`}
            className="flex items-center gap-1.5 text-sm font-medium text-primary transition-smooth hover:gap-2"
          >
            Read More
            <Icon name="ArrowRightIcon" size={16} variant="outline" />
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BlogCard;
